<template>
  <div class="user-form-container">
    <v-form ref="form" @submit.prevent="onSubmit" class="user-form">

      <!-- Header -->
      <header class="form-header">
        <div class="header-content">
          <div class="header-left">
            <div class="user-avatar">
              <span class="avatar-initials">{{ userInitials }}</span>
            </div>
            <div class="header-info">
              <h1 class="form-title">
                {{ action === 'create' ? 'Nouvel Utilisateur' : 'Modifier l\'Utilisateur' }}
              </h1>
              <p class="form-subtitle" v-if="value.email">{{ value.email }}</p>
            </div>
          </div>

          <!-- Header Actions -->
          <div class="header-actions">
            <button type="button" class="btn btn-ghost" @click="cancel">
              <span class="material-symbols-outlined">close</span>
              <span class="btn-text">Annuler</span>
            </button>
          </div>
        </div>
      </header>

      <!-- Form Body -->
      <div class="form-body">
        <!-- Personal Information Section -->
        <section class="form-section">
          <div class="section-header">
            <span class="material-symbols-outlined">person</span>
            <h2>Informations personnelles</h2>
          </div>

          <div class="fields-grid">
            <div class="field-group">
              <label class="field-label required">Nom</label>
              <input
                  type="text"
                  v-model="value.lastname"
                  class="form-input"
                  placeholder="Entrez le nom..."
              />
            </div>

            <div class="field-group">
              <label class="field-label required">Prénom</label>
              <input
                  type="text"
                  v-model="value.firstname"
                  class="form-input"
                  placeholder="Entrez le prénom..."
              />
            </div>

            <div class="field-group">
              <label class="field-label required">E-mail</label>
              <div class="input-with-icon">
                <span class="material-symbols-outlined">mail</span>
                <input
                    type="email"
                    v-model="value.email"
                    class="form-input with-icon"
                    placeholder="exemple@email.com"
                />
              </div>
            </div>

            <div class="field-group">
              <label class="field-label">Téléphone</label>
              <div class="input-with-icon">
                <span class="material-symbols-outlined">phone</span>
                <input
                    type="tel"
                    v-model="value.phone"
                    class="form-input with-icon"
                    placeholder="+33 6 00 00 00 00"
                />
              </div>
            </div>
          </div>
        </section>

        <!-- Preferences Section -->
        <section class="form-section">
          <div class="section-header">
            <span class="material-symbols-outlined">tune</span>
            <h2>Préférences</h2>
          </div>

          <div class="fields-grid">
            <div class="field-group">
              <label class="field-label">Page d'accueil</label>
              <v-select
                  v-model="value.homeType"
                  :items="homeTypeOptions"
                  solo
                  flat
                  hide-details
                  dense
                  placeholder="Sélectionner..."
                  class="custom-select"
              ></v-select>
            </div>

            <div class="field-group full-width">
              <label class="field-label">Numéros de téléphone des collègues</label>
              <v-combobox
                  v-model="value.coworkers_phones"
                  :items="items"
                  multiple
                  chips
                  small-chips
                  deletable-chips
                  clearable
                  solo
                  flat
                  hide-details
                  dense
                  placeholder="Ajouter des numéros (sans +33)..."
                  class="custom-combobox"
              ></v-combobox>
            </div>
          </div>

          <!-- Notification Toggles -->
          <div class="toggles-row">
            <div class="toggle-card" :class="{ active: value.optin_mail }">
              <div class="toggle-info">
                <span class="material-symbols-outlined">mail</span>
                <div>
                  <h4>Notifications e-mail</h4>
                  <p>Recevoir les alertes par e-mail</p>
                </div>
              </div>
              <v-switch
                  v-model="value.optin_mail"
                  :true-value="1"
                  :false-value="0"
                  inset
                  hide-details
                  color="#2d5a3d"
              ></v-switch>
            </div>

            <div class="toggle-card" :class="{ active: value.optin_sms }">
              <div class="toggle-info">
                <span class="material-symbols-outlined">sms</span>
                <div>
                  <h4>Notifications SMS</h4>
                  <p>Recevoir les alertes par SMS</p>
                </div>
              </div>
              <v-switch
                  v-model="value.optin_sms"
                  :true-value="1"
                  :false-value="0"
                  inset
                  hide-details
                  color="#2d5a3d"
              ></v-switch>
            </div>
          </div>
        </section>

        <!-- Role & Security Section -->
        <section class="form-section">
          <div class="section-header">
            <span class="material-symbols-outlined">shield_person</span>
            <h2>Rôle & Sécurité</h2>
          </div>

          <div class="fields-grid">
            <!-- Role Selection (Admin only) -->
            <div class="field-group" style="grid-column: span 2;" v-if="$acl.check('isAdmin')">
              <label class="field-label required">Rôle</label>
              <div class="role-grid">
                <button
                    v-for="role in roleOptions"
                    :key="role.value"
                    type="button"
                    class="role-option"
                    :class="{ selected: value.role === role.value }"
                    @click="value.role = role.value"
                >
                  <span class="material-symbols-outlined">{{ role.icon }}</span>
                  <span class="role-name">{{ role.text }}</span>
                </button>
              </div>
            </div>
          </div>

          <!-- Password Section - Create -->
          <div class="password-section" v-if="action === 'create'">
            <div class="fields-grid">
              <div class="field-group">
                <label class="field-label">Mot de passe</label>
                <div class="password-input-wrapper">
                  <input
                      :type="show_password ? 'text' : 'password'"
                      v-model="password"
                      class="form-input"
                      placeholder="8 caractères min."
                  />
                  <button
                      type="button"
                      class="password-toggle"
                      @click="toggleShowPassword"
                  >
                    <span class="material-symbols-outlined">
                      {{ show_password ? 'visibility_off' : 'visibility' }}
                    </span>
                  </button>
                </div>
                <span class="field-hint">Minimum 8 caractères</span>
              </div>

              <div class="field-group">
                <label class="field-label">Confirmer le mot de passe</label>
                <div class="password-input-wrapper">
                  <input
                      :type="show_confirm ? 'text' : 'password'"
                      v-model="confirm"
                      class="form-input"
                      placeholder="Confirmer..."
                  />
                  <button
                      type="button"
                      class="password-toggle"
                      @click="toggleShowConfirm"
                  >
                    <span class="material-symbols-outlined">
                      {{ show_confirm ? 'visibility_off' : 'visibility' }}
                    </span>
                  </button>
                </div>
                <span class="field-hint password-match" :class="{ valid: passwordsMatch, invalid: password && confirm && !passwordsMatch }">
                  <span class="material-symbols-outlined" v-if="password && confirm">
                    {{ passwordsMatch ? 'check_circle' : 'error' }}
                  </span>
                  {{ passwordMatchText }}
                </span>
              </div>
            </div>
          </div>

          <!-- Password Section - Update -->
          <div class="password-section" v-if="action === 'update'">
            <div class="password-update-header">
              <h4>Modifier le mot de passe</h4>
              <p>Laissez vide pour conserver le mot de passe actuel</p>
            </div>

            <div class="fields-grid three-col">
              <div class="field-group">
                <label class="field-label">Ancien mot de passe</label>
                <div class="password-input-wrapper">
                  <input
                      :type="show_old_password ? 'text' : 'password'"
                      v-model="old_password"
                      class="form-input"
                      placeholder="••••••••"
                  />
                  <button
                      type="button"
                      class="password-toggle"
                      @click="toggleShowOldPassword"
                  >
                    <span class="material-symbols-outlined">
                      {{ show_old_password ? 'visibility_off' : 'visibility' }}
                    </span>
                  </button>
                </div>
              </div>

              <div class="field-group">
                <label class="field-label">Nouveau mot de passe</label>
                <div class="password-input-wrapper">
                  <input
                      :type="show_password ? 'text' : 'password'"
                      v-model="password"
                      class="form-input"
                      placeholder="8 caractères min."
                  />
                  <button
                      type="button"
                      class="password-toggle"
                      @click="toggleShowPassword"
                  >
                    <span class="material-symbols-outlined">
                      {{ show_password ? 'visibility_off' : 'visibility' }}
                    </span>
                  </button>
                </div>
              </div>

              <div class="field-group">
                <label class="field-label">Confirmer</label>
                <div class="password-input-wrapper">
                  <input
                      :type="show_confirm ? 'text' : 'password'"
                      v-model="confirm"
                      class="form-input"
                      placeholder="Confirmer..."
                  />
                  <button
                      type="button"
                      class="password-toggle"
                      @click="toggleShowConfirm"
                  >
                    <span class="material-symbols-outlined">
                      {{ show_confirm ? 'visibility_off' : 'visibility' }}
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- Access Control Section (Admin only) -->
        <section class="form-section" v-if="$acl.check('isAdmin') && (show_clients || show_sites || show_users)">
          <div class="section-header">
            <span class="material-symbols-outlined">admin_panel_settings</span>
            <h2>Contrôle d'accès</h2>
          </div>

          <!-- Clients -->
          <div class="access-card" v-if="show_clients">
            <div class="access-header">
              <span class="material-symbols-outlined">business</span>
              <div>
                <h4>Clients</h4>
                <p>Clients accessibles par cet utilisateur</p>
              </div>
            </div>
            <v-autocomplete
                v-model="value.clients"
                :items="allClients"
                item-text="name"
                item-value="id"
                multiple
                chips
                small-chips
                deletable-chips
                return-object
                solo
                flat
                hide-details
                dense
                placeholder="Sélectionner les clients..."
                class="custom-autocomplete"
            ></v-autocomplete>
          </div>

          <!-- Sites -->
          <div class="access-card" v-if="show_sites">
            <div class="access-header">
              <span class="material-symbols-outlined">domain</span>
              <div>
                <h4>Sites</h4>
                <p>Univers accessibles par cet utilisateur</p>
              </div>
            </div>
            <v-autocomplete
                v-model="value.sitation"
                :items="allSites"
                item-text="complete_name"
                item-value="id"
                multiple
                chips
                small-chips
                deletable-chips
                return-object
                solo
                flat
                hide-details
                dense
                placeholder="Sélectionner les sites..."
                class="custom-autocomplete"
            ></v-autocomplete>
          </div>

          <!-- Users -->
          <div class="access-card" v-if="show_users">
            <div class="access-header">
              <span class="material-symbols-outlined">group</span>
              <div>
                <h4>Utilisateurs</h4>
                <p>Utilisateurs gérés par cet utilisateur</p>
              </div>
            </div>
            <v-autocomplete
                v-model="value.users"
                :items="allUsers"
                item-text="complete_name"
                item-value="id"
                multiple
                chips
                small-chips
                deletable-chips
                return-object
                solo
                flat
                hide-details
                dense
                placeholder="Sélectionner les utilisateurs..."
                class="custom-autocomplete"
            ></v-autocomplete>
          </div>
        </section>
      </div>

      <!-- Footer -->
      <footer class="form-footer">
        <div class="footer-content">
          <div class="footer-left">
            <span class="save-hint">
              <span class="material-symbols-outlined">keyboard</span>
              Entrée pour sauvegarder
            </span>
          </div>
          <div class="footer-actions">
            <button type="button" class="btn btn-ghost" @click="cancel">
              Annuler
            </button>
            <button
                type="submit"
                class="btn btn-primary"
                :disabled="loading"
                :class="{ loading }"
            >
              <span class="material-symbols-outlined" v-if="!loading">save</span>
              <span class="loader-spinner" v-else></span>
              {{ loading ? 'Sauvegarde...' : 'Sauvegarder' }}
            </button>
          </div>
        </div>
      </footer>
    </v-form>
  </div>
</template>

<script>
export default {
  name: 'UserForm',

  props: {
    value: Object,
    action: {
      type: String,
      default: "update",
    },
    showEditSites: {
      type: Boolean,
      default: false,
    },
    showEditClients: {
      type: Boolean,
      default: false,
    },
    showEditUsers: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    if (!this.value.role) this.value.role = "user";

    return {
      old_password: "",
      password: "",
      confirm: "",
      show_old_password: false,
      show_password: false,
      show_confirm: false,
      show_sites: this.showEditSites,
      show_clients: this.showEditClients,
      show_users: this.showEditUsers,
      updatePassword: false,
      loading: false,
      items: [],
      rules: {
        required: (value) => !!value || "Champ requis",
        minlength: (value) => value.length >= 8 || "8 caractères min.",
      },
      allSites: [],
      allClients: [],
      allUsers: [],

      // Options
      homeTypeOptions: [
        { value: 'S1', text: 'Accueil par défaut' },
        { value: 'S2', text: 'Module de dégustations' },
        { value: 'S3', text: 'Sécurité CO2' }
      ],
      roleOptions: [
        { value: 'user', text: 'Utilisateur', icon: 'person' },
        { value: 'user_conf', text: 'Configurateur', icon: 'settings' },
        { value: 'user_super', text: 'Multi-client', icon: 'group' },
        { value: 'user_admin', text: 'Administrateur', icon: 'admin_panel_settings' },
        { value: 'admin', text: 'Super Admin', icon: 'shield_person' },
      ],
    };
  },

  computed: {
    userInitials() {
      const first = this.value.firstname ? this.value.firstname[0] : '';
      const last = this.value.lastname ? this.value.lastname[0] : '';
      return (first + last).toUpperCase() || '?';
    },

    passwordsMatch() {
      return this.password && this.confirm && this.password === this.confirm;
    },

    passwordMatchText() {
      if (!this.password || !this.confirm) return '';
      return this.passwordsMatch ? 'Les mots de passe correspondent' : 'Les mots de passe ne correspondent pas';
    },
  },

  watch: {
    value: {
      deep: true,
      handler() {
        this.updateForm();
        if (this.$refs.form) {
          this.$refs.form.resetValidation();
        }
      },
    },
  },

  updated() {
    if (typeof this.value.coworkers_phones === "string") {
      this.value.coworkers_phones = JSON.parse(this.value.coworkers_phones);
    }
  },

  mounted() {
    this.fetchClients();
    if (this.canChooseSite()) {
      this.fetchSites();
    }
    if (this.show_users) {
      this.fetchUsers();
    }
  },

  methods: {
    canChooseSite() {
      return this.value.clients !== undefined && this.value.clients.length > 0;
    },

    updateForm() {
      const canChooseSite = this.canChooseSite();

      if (this.value.role === "user" || this.value.role === "user_conf" || this.value.role === "user_super") {
        this.show_sites = canChooseSite;
        this.show_clients = true;
        this.show_users = false;
      } else if (this.value.role === "admin" || this.value.role === "user_admin") {
        this.show_sites = canChooseSite;
        this.show_clients = true;
        this.show_users = true;
      }

      if (canChooseSite) {
        this.fetchSites();
      }
      if (this.show_users) {
        this.fetchUsers();
      }
    },

    toggleShowConfirm() {
      this.show_confirm = !this.show_confirm;
    },

    toggleShowPassword() {
      this.show_password = !this.show_password;
    },

    toggleShowOldPassword() {
      this.show_old_password = !this.show_old_password;
    },

    fetchClients() {
      this.$store
          .dispatch("clientsRequest", {})
          .then((result) => {
            this.allClients = result.data;
          })
          .catch(() => {});
    },

    fetchSites() {
      this.$store
          .dispatch("sitesRequest", {})
          .then((result) => {
            this.allSites = result.data.filter((site) => {
              return this.value.clients.map((c) => c.id).includes(site.client_id);
            });
          })
          .catch(() => {});
    },

    fetchUsers() {
      this.$store
          .dispatch("usersRequest", {})
          .then((result) => {
            this.allUsers = result.data;
          })
          .catch(() => {});
    },

    onSubmit() {
      if (this.$refs.form.validate()) {
        this.loading = true;

        if (this.old_password) {
          this.value.old_password = this.old_password;
        }

        if (this.password && this.confirm) {
          this.value.password = this.password;
          this.value.password_confirmation = this.confirm;
        }

        let data = Object.assign({}, this.value);

        if (this.value.defaultclient) {
          data.defaultclient = this.value.defaultclient;
        }
        if (this.value.clients) {
          data.clients = this.value.clients.map((c) => c.id);
        }
        if (this.value.sitation) {
          this.value.sites = this.value.sitation.filter((site) => {
            return this.value.clients.map((c) => c.id).includes(site.client_id);
          });
          data.sites = this.value.sitation.map((s) => s.id);
        }
        if (this.value.users) {
          data.users = this.value.users.map((u) => u.id);
        }

        let request = "userCreateRequest";
        let queries = data;

        if (this.action === "update") {
          request = "userEditRequest";
          queries = { id: this.value.id, datas: data };
        }

        this.$store
            .dispatch(request, queries)
            .then((user) => {
              this.$refs.form.resetValidation();
              this.loading = false;
              this.$store.commit("setSnack", {
                type: "success",
                msg: "Utilisateur enregistré !",
              });
              this.$emit("savedForm", user);
            })
            .catch((err) => {
              this.loading = false;
              if (err.response && err.response.status !== 403) {
                this.$store.commit("setSnack", {
                  type: "red",
                  msg: err.response.data,
                });
              }
            });
      }
    },

    cancel() {
      this.$refs.form.reset();
      this.$emit("canceledForm");
    },
  },
};
</script>

<style scoped>
/* ============================================
   CSS VARIABLES
   ============================================ */
.user-form-container {
  /* Primary greens - refined palette */
  --color-primary: #2d5a3d;
  --color-primary-light: #3d7a52;
  --color-primary-lighter: #5a9e6f;
  --color-primary-dark: #1e3d29;
  --color-primary-bg: #e8f0eb;

  /* Accent */
  --color-accent: #5a9e6f;
  --color-accent-light: #a8d4b8;

  /* Surfaces */
  --color-surface: #ffffff;
  --color-surface-secondary: #fafbfa;
  --color-surface-hover: #f0f4f1;
  --color-background: #f5f7f6;

  /* Borders */
  --color-border: #e2e8e4;
  --color-border-light: #f0f4f1;

  /* Text */
  --color-text-primary: #1a2e1f;
  --color-text-secondary: #5a6b5e;
  --color-text-muted: #8a998e;

  /* Status */
  --color-danger: #dc2626;
  --color-danger-bg: #fef2f2;
  --color-warning: #d97706;
  --color-warning-bg: #fffbeb;
  --color-success: #059669;
  --color-success-bg: #ecfdf5;

  /* Shadows */
  --shadow-xs: 0 1px 2px rgba(45, 90, 61, 0.04);
  --shadow-sm: 0 1px 2px rgba(45, 90, 61, 0.05);
  --shadow-md: 0 4px 12px rgba(45, 90, 61, 0.08);
  --shadow-lg: 0 12px 40px rgba(45, 90, 61, 0.12);

  /* Radii */
  --radius-sm: 6px;
  --radius-md: 10px;
  --radius-lg: 16px;
  --radius-xl: 20px;

  /* Transitions */
  --transition-fast: 150ms ease;
  --transition-normal: 200ms cubic-bezier(0.4, 0, 0.2, 1);
  --transition-smooth: 300ms cubic-bezier(0.4, 0, 0.2, 1);

  font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;

  background-color: white;
}

.user-form {
  display: flex;
  flex-direction: column;
  height: 100%;
}

/* ============================================
   HEADER
   ============================================ */
.form-header {
  background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-light) 100%);
  padding: 24px 28px;
  flex-shrink: 0;
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.user-avatar {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.avatar-initials {
  font-size: 1.25rem;
  font-weight: 600;
  color: white;
}

.header-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.form-title {
  font-size: 1.35rem;
  font-weight: 600;
  color: white;
  margin: 0;
  letter-spacing: -0.02em;
}

.form-subtitle {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.75);
  margin: 0;
}

/* ============================================
   FORM BODY
   ============================================ */
.form-body {
  flex: 1;
  overflow-y: auto;
  padding: 24px 28px;
  display: flex;
  flex-direction: column;
  gap: 28px;
}

/* ============================================
   SECTIONS
   ============================================ */
.form-section {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--color-border-light);
}

.section-header .material-symbols-outlined {
  font-size: 22px;
  color: var(--color-accent);
}

.section-header h2 {
  font-size: 1rem;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0;
}

/* ============================================
   FIELDS GRID
   ============================================ */
.fields-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
}

@media (min-width: 640px) {
  .fields-grid {
    grid-template-columns: 1fr 1fr;
  }

  .fields-grid.three-col {
    grid-template-columns: 1fr 1fr 1fr;
  }
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field-group.full-width {
  grid-column: 1 / -1;
}

.field-label {
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--color-text-secondary);
}

.field-label.required::after {
  content: ' *';
  color: var(--color-danger);
}

.field-hint {
  font-size: 0.75rem;
  color: var(--color-text-muted);
}

.field-hint.password-match {
  display: flex;
  align-items: center;
  gap: 4px;
}

.field-hint.password-match .material-symbols-outlined {
  font-size: 14px;
}

.field-hint.password-match.valid {
  color: var(--color-success);
}

.field-hint.password-match.invalid {
  color: var(--color-danger);
}

/* ============================================
   FORM INPUTS
   ============================================ */
.form-input {
  width: 100%;
  padding: 12px 14px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  font-size: 0.9375rem;
  color: var(--color-text-primary);
  background: var(--color-surface);
  transition: var(--transition-normal);
  font-family: inherit;
}

.form-input:hover {
  border-color: var(--color-primary-lighter);
}

.form-input:focus {
  outline: none;
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px var(--color-primary-bg);
}

.form-input::placeholder {
  color: var(--color-text-muted);
}

.form-input.with-icon {
  padding-left: 42px;
}

/* Input with Icon */
.input-with-icon {
  position: relative;
}

.input-with-icon .material-symbols-outlined {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 18px;
  color: var(--color-text-muted);
  pointer-events: none;
}

/* Password Input */
.password-input-wrapper {
  position: relative;
}

.password-input-wrapper .form-input {
  padding-right: 44px;
}

.password-toggle {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: var(--color-text-muted);
  border-radius: var(--radius-sm);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: var(--transition-fast);
}

.password-toggle:hover {
  background: var(--color-surface-hover);
  color: var(--color-text-secondary);
}

.password-toggle .material-symbols-outlined {
  font-size: 20px;
}

/* Password Section */
.password-section {
  margin-top: 8px;
}

.password-update-header {
  margin-bottom: 16px;
  padding: 12px 16px;
  background: var(--color-surface-secondary);
  border-radius: var(--radius-sm);
}

.password-update-header h4 {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0 0 4px;
}

.password-update-header p {
  font-size: 0.8125rem;
  color: var(--color-text-muted);
  margin: 0;
}

/* ============================================
   TOGGLE CARDS
   ============================================ */
.toggles-row {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  margin-top: 8px;
}

@media (min-width: 640px) {
  .toggles-row {
    grid-template-columns: 1fr 1fr;
  }
}

.toggle-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background: var(--color-surface-secondary);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  transition: var(--transition-normal);
}

.toggle-card.active {
  background: var(--color-primary-bg);
  border-color: var(--color-accent);
}

.toggle-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.toggle-info .material-symbols-outlined {
  font-size: 22px;
  color: var(--color-accent);
}

.toggle-info h4 {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0;
}

.toggle-info p {
  font-size: 0.75rem;
  color: var(--color-text-muted);
  margin: 2px 0 0;
}

/* ============================================
   ROLE SELECTOR
   ============================================ */
.role-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 10px;
  margin-top: 4px;
}

.role-option {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 16px 12px;
  background: var(--color-surface-secondary);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: var(--transition-normal);
}

.role-option:hover {
  border-color: var(--color-primary-lighter);
  background: var(--color-surface-hover);
}

.role-option.selected {
  background: var(--color-primary-bg);
  border-color: var(--color-primary);
}

.role-option .material-symbols-outlined {
  font-size: 24px;
  color: var(--color-text-muted);
}

.role-option.selected .material-symbols-outlined {
  color: var(--color-primary);
}

.role-name {
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--color-text-secondary);
  text-align: center;
}

.role-option.selected .role-name {
  color: var(--color-primary);
}

/* ============================================
   ACCESS CARDS
   ============================================ */
.access-card {
  padding: 20px;
  background: var(--color-surface-secondary);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  margin-bottom: 12px;
}

.access-card:last-child {
  margin-bottom: 0;
}

.access-header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 14px;
}

.access-header .material-symbols-outlined {
  font-size: 22px;
  color: var(--color-accent);
  margin-top: 2px;
}

.access-header h4 {
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0;
}

.access-header p {
  font-size: 0.8125rem;
  color: var(--color-text-muted);
  margin: 2px 0 0;
}

/* ============================================
   BUTTONS
   ============================================ */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 20px;
  border-radius: var(--radius-sm);
  font-size: 0.9375rem;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: var(--transition-normal);
  white-space: nowrap;
  font-family: inherit;
}

.btn .material-symbols-outlined {
  font-size: 18px;
}

.btn-primary {
  background: var(--color-primary);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: var(--color-primary-light);
  transform: translateY(-1px);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.btn-primary.loading {
  pointer-events: none;
}

.btn-ghost {
  background: var(--color-surface-secondary);
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border);
}

.btn-ghost:hover {
  background: var(--color-surface-hover);
  border-color: var(--color-primary-lighter);
}

/* Loader */
.loader-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* ============================================
   FOOTER
   ============================================ */
.form-footer {
  padding: 16px 28px;
  background: var(--color-surface);
  border-top: 1px solid var(--color-border);
  flex-shrink: 0;
}

.footer-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.footer-left {
  display: flex;
  align-items: center;
}

.save-hint {
  display: none;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  color: var(--color-text-muted);
}

@media (min-width: 640px) {
  .save-hint {
    display: flex;
  }
}

.save-hint .material-symbols-outlined {
  font-size: 14px;
}

.footer-actions {
  display: flex;
  gap: 10px;
}

/* ============================================
   VUETIFY OVERRIDES
   ============================================ */
.custom-select >>> .v-input__slot,
.custom-combobox >>> .v-input__slot,
.custom-autocomplete >>> .v-input__slot {
  background: var(--color-surface) !important;
  border: 1px solid var(--color-border) !important;
  border-radius: var(--radius-sm) !important;
  box-shadow: none !important;
  min-height: 46px !important;
}

.custom-select >>> .v-input__slot:hover,
.custom-combobox >>> .v-input__slot:hover,
.custom-autocomplete >>> .v-input__slot:hover {
  border-color: var(--color-primary-lighter) !important;
}

.custom-select >>> .v-input--is-focused .v-input__slot,
.custom-combobox >>> .v-input--is-focused .v-input__slot,
.custom-autocomplete >>> .v-input--is-focused .v-input__slot {
  border-color: var(--color-primary) !important;
  box-shadow: 0 0 0 3px var(--color-primary-bg) !important;
}

.custom-combobox >>> .v-chip,
.custom-autocomplete >>> .v-chip {
  background: var(--color-primary) !important;
  color: white !important;
  margin: 7px 5px;
  padding: 6px 9px;
  font-size: 14px;
}

.custom-combobox >>> .v-chip .v-chip__close,
.custom-autocomplete >>> .v-chip .v-chip__close {
  color: rgba(255, 255, 255, 0.7) !important;
}

/* Switch overrides */
>>> .v-input--switch .v-input--switch__track {
  opacity: 0.4;
}

>>> .v-input--switch.v-input--is-label-active .v-input--switch__track {
  opacity: 0.7;
}

>>> .v-input--selection-controls {
  margin: 0;
  padding: 0;
}

/* ============================================
   MATERIAL SYMBOLS
   ============================================ */
.material-symbols-outlined {
  font-family: 'Material Symbols Outlined';
  font-variation-settings:
      'FILL' 0,
      'wght' 400,
      'GRAD' 0,
      'opsz' 24;
}
</style>